import { ReactNode, useEffect, useState } from 'react';
import { DefaultEditor } from '@drodil/backstage-plugin-toolbox';
import { makeStyles, useTheme, Paper, Typography } from '@material-ui/core';
import mermaid from 'mermaid';

const useStyles = makeStyles(theme => ({
  preview: {
    width: '100%',
    minHeight: theme.spacing(40),
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    '& svg': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
  error: {
    color: theme.palette.error.main,
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
  },
}));

const sample = `graph TD
  A[Client] -->|request| B(API Gateway)
  B --> C{Auth ok?}
  C -->|yes| D[Lambda]
  C -->|no| E[401]
  D --> F[(Database)]`;

let renderSeq = 0;

export const MermaidPreviewTool = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');
  const isDark = theme.palette.type === 'dark';

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: isDark ? 'dark' : 'default',
      securityLevel: 'strict',
    });
  }, [isDark]);

  useEffect(() => {
    let cancelled = false;
    const code = input.trim();
    if (!code) {
      setSvg('');
      setError('');
      return undefined;
    }
    const id = `mermaid-preview-${(renderSeq += 1)}`;
    mermaid
      .render(id, code)
      .then(result => {
        if (!cancelled) {
          setSvg(result.svg);
          setError('');
        }
      })
      .catch(e => {
        if (!cancelled) {
          setSvg('');
          setError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [input, isDark]);

  let previewContent: ReactNode;
  if (error) {
    previewContent = (
      <Typography className={classes.error}>{error}</Typography>
    );
  } else if (svg) {
    // eslint-disable-next-line react/no-danger
    previewContent = <div dangerouslySetInnerHTML={{ __html: svg }} />;
  } else {
    previewContent = (
      <Typography color="textSecondary">
        Diagram preview will appear here
      </Typography>
    );
  }

  const preview = (
    <Paper variant="outlined" className={classes.preview}>
      {previewContent}
    </Paper>
  );

  return (
    <DefaultEditor
      input={input}
      setInput={setInput}
      output={svg}
      sample={sample}
      inputLabel="Mermaid code"
      outputLabel="Preview"
      rightContent={preview}
      allowFileUpload
      acceptFileTypes=".mmd,.mermaid,.txt"
      allowFileDownload
      downloadFileName="diagram.svg"
      downloadFileType="image/svg+xml"
    />
  );
};

export default MermaidPreviewTool;
